// Copyright © 2019-2026 Focal Systems. All rights reserved.

import { Link } from "@tanstack/react-router";
import type { LucideIcon } from "lucide-react";
import { ArrowDown, Ban, Check, CircleSlash, CornerDownRight, Minus, Power, TriangleAlert } from "lucide-react";
import { useTranslation } from "react-i18next";
import type { OctoEvaluatedRule, OctoEvaluateResult, OctoRuleEvaluation } from "@/api/hooks/use-octo-evaluate";
import { CodeHighlight } from "@/components/shared/code-highlight";
import { Badge } from "@/components/ui/badge";
import { OctoOutcomeBadge } from "@/features/octo/octo-rule-badges";
import { cn } from "@/lib/utils";

interface EvaluationStyle {
  icon: LucideIcon;
  /** Card treatment — the decisive states get a coloured edge, the inert ones recede. */
  card: string;
  badge: string;
  /** Whether the node reads as "the engine never got here". */
  muted: boolean;
}

const EVALUATION_STYLE: Record<OctoRuleEvaluation, EvaluationStyle> = {
  matched: {
    icon: Check,
    card: "border-green-300 bg-[#E6F4EA]/40 dark:border-green-900 dark:bg-green-950/30",
    badge: "bg-[#E6F4EA] border-green-200 text-foreground dark:bg-green-950 dark:text-green-300 dark:border-green-900",
    muted: false,
  },
  notMatched: {
    icon: Minus,
    card: "border-border bg-muted/50",
    badge: "bg-muted text-muted-foreground border-border",
    muted: false,
  },
  fallback: {
    icon: CornerDownRight,
    card: "border-blue-300 bg-[#E3F1FF]/40 dark:border-blue-900 dark:bg-blue-950/30",
    badge: "bg-[#E3F1FF] border-blue-200 text-foreground dark:bg-blue-950 dark:text-blue-300 dark:border-blue-900",
    muted: false,
  },
  skippedInactive: {
    icon: Power,
    card: "border-dashed border-border bg-muted/50",
    badge: "bg-muted text-muted-foreground border-border",
    muted: true,
  },
  notReached: {
    icon: CircleSlash,
    card: "border-dashed border-border bg-muted/50",
    badge: "bg-muted text-muted-foreground border-border",
    muted: true,
  },
  error: {
    icon: TriangleAlert,
    card: "border-destructive/60 bg-destructive/5",
    badge: "bg-destructive/10 border-destructive/30 text-destructive",
    muted: false,
  },
  notEvaluated: {
    icon: Minus,
    card: "border-border bg-muted/50",
    badge: "bg-muted text-muted-foreground border-border",
    muted: false,
  },
};

/** States after which the engine stops walking the chain. */
const TERMINAL_EVALUATIONS: OctoRuleEvaluation[] = ["matched", "fallback", "error"];

export function OctoFlowDiagram({ result }: { result: OctoEvaluateResult }) {
  const { t } = useTranslation("back-office");

  if (result.rules.length === 0) {
    return (
      <div className="rounded-md border border-dashed p-6 text-center text-sm text-muted-foreground">
        {t("octoRules.evaluate.noRules")}
      </div>
    );
  }

  return (
    <div className="flex flex-col">
      {result.rules.map((rule, index) => {
        const stopsHere = TERMINAL_EVALUATIONS.includes(rule.evaluation);
        return (
          <div key={rule.octoRuleId} className="flex flex-col">
            {index > 0 && <FlowConnector faded={EVALUATION_STYLE[rule.evaluation].muted} />}
            <FlowNode rule={rule} position={index + 1} />
            {stopsHere && index < result.rules.length - 1 && <StopMarker />}
          </div>
        );
      })}
      <FlowConnector faded />
      <OutcomeNode result={result} />
    </div>
  );
}

function FlowConnector({ faded }: { faded?: boolean }) {
  return (
    <div className="flex h-5 items-center justify-center" aria-hidden>
      <div className={cn("h-full w-px bg-border", faded && "opacity-50")} />
    </div>
  );
}

/** Marks where the engine stopped walking — everything below it is dead weight. */
function StopMarker() {
  const { t } = useTranslation("back-office");
  return (
    <div className="flex items-center gap-2 py-1.5 pl-2">
      <Ban className="h-3.5 w-3.5 shrink-0 text-muted-foreground" />
      <span className="text-xs text-muted-foreground">{t("octoRules.evaluate.stoppedHere")}</span>
      <div className="h-px flex-1 bg-border" />
    </div>
  );
}

function FlowNode({ rule, position }: { rule: OctoEvaluatedRule; position: number }) {
  const { t } = useTranslation("back-office");
  const style = EVALUATION_STYLE[rule.evaluation];
  const Icon = style.icon;

  return (
    <div className={cn("flex gap-3 rounded-md border p-3", style.card, style.muted && "opacity-60")}>
      <div className="flex flex-col items-center gap-1 pt-0.5">
        <span className="flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-muted font-mono text-xs tabular-nums text-muted-foreground">
          {position}
        </span>
      </div>

      <div className="flex min-w-0 flex-1 flex-col gap-1.5">
        {/* Single line by design: the badges and verdict keep their size, and a long rule name
            truncates rather than pushing the verdict onto a row of its own. */}
        <div className="flex items-center gap-2">
          <Link
            to="/octo/rules/$ruleId"
            params={{ ruleId: String(rule.octoRuleId) }}
            target="_blank"
            title={rule.name || undefined}
            // text-sm to match the rule name in the list, where the table root sets the size.
            className="min-w-0 truncate text-sm font-medium text-primary hover:underline"
          >
            {rule.name || t("octoRules.unnamed", { id: rule.octoRuleId })}
          </Link>
          <Badge variant="outline" className="shrink-0 font-normal lowercase">
            {t(`octoRules.evaluate.scope.${rule.scope}`)}
          </Badge>
          <OctoOutcomeBadge outcome={rule.outcome} className="shrink-0" />
          {/* Bare number — the label lives in the tooltip so the row stays compact. Mono/tabular
              so priorities line up vertically down the flow. */}
          <Badge
            variant="secondary"
            title={t("octoRules.evaluate.priorityLabel", { priority: rule.priority })}
            className="shrink-0 font-mono font-normal tabular-nums"
          >
            {rule.priority}
          </Badge>
          <Badge variant="secondary" className={cn("ml-auto shrink-0 gap-1 font-normal", style.badge)}>
            <Icon className="h-3 w-3" />
            {t(`octoRules.evaluate.evaluation.${rule.evaluation}`)}
          </Badge>
        </div>

        {rule.description && <p className="text-xs text-muted-foreground">{rule.description}</p>}

        {rule.predicate ? (
          <CodeHighlight
            value={rule.predicate}
            lang="cel"
            copyable={false}
            // Tighter than the detail page's default padding — these sit inside a compact node —
            // and inverted: the tile carries the muted grey, so the code block takes the dialog
            // surface to stay legible against it.
            className="[&_pre]:min-w-0 [&_pre]:!bg-background [&_pre]:!p-2.5 [&_pre]:whitespace-pre-wrap [&_pre]:break-words"
          />
        ) : (
          <span className="text-xs text-muted-foreground italic">{t("octoRules.evaluate.noPredicate")}</span>
        )}

        {rule.error && <p className="break-words text-xs text-destructive">{rule.error}</p>}

        {rule.outOfDateRange && (
          <p className="flex items-start gap-1.5 text-xs text-amber-600 dark:text-amber-500">
            <TriangleAlert className="mt-px h-3.5 w-3.5 shrink-0" />
            {t("octoRules.evaluate.outOfDateRange")}
          </p>
        )}
      </div>
    </div>
  );
}

/** Terminal node: the decision the engine lands on, and what settled it. */
function OutcomeNode({ result }: { result: OctoEvaluateResult }) {
  const { t } = useTranslation("back-office");
  const failed = result.reason === "compileError" || result.reason === "evaluationError";
  const decided = result.rules.find((x) => x.octoRuleId === result.decidedByRuleId);

  return (
    <div
      className={cn(
        "flex flex-col gap-1.5 rounded-md border-2 p-3",
        failed && "border-destructive/60 bg-destructive/5",
        !failed && result.outcome === "allow" && "border-green-300 dark:border-green-900",
        !failed && result.outcome === "deny" && "border-red-300 dark:border-red-900",
        !failed && !result.outcome && "border-dashed border-border",
      )}
    >
      <div className="flex flex-wrap items-center gap-2">
        <ArrowDown className="h-4 w-4 shrink-0 text-muted-foreground" />
        <span className="font-medium">{t("octoRules.evaluate.outcomeTitle")}</span>
        {result.outcome ? (
          <OctoOutcomeBadge outcome={result.outcome} />
        ) : (
          <Badge variant="outline" className="font-normal">
            {t("octoRules.evaluate.outcomeNone")}
          </Badge>
        )}
      </div>
      <p className="text-sm text-muted-foreground">
        {t(`octoRules.evaluate.reason.${result.reason}`, { rule: decided?.name || result.decidedByRuleId })}
      </p>
      {result.error && <p className="break-words text-xs text-destructive">{result.error}</p>}
      {/* The stage effect is what an operator actually cares about: a denied creation means the
          task is never made. Spelled out per stage rather than left as an abstract allow/deny. */}
      {result.outcome && (
        <p className="text-xs text-muted-foreground">
          {t(`octoRules.evaluate.effect.${result.stage}.${result.outcome}`)}
        </p>
      )}
    </div>
  );
}
